import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Pencil, Power } from 'lucide-react';
import {
  clientInputSchema,
  type ClientInput,
} from '@timesheet/shared';
import { useCrud } from '@/hooks/useCrud';
import { Modal } from '@/components/ui/Modal';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Badge } from '@/components/ui/Badge';
import { Alert } from '@/components/ui/Alert';
import { CrudHeader } from '@/components/crud/CrudHeader';
import { BulkUpload } from '@/components/crud/BulkUpload';

interface ClientRow {
  id: string;
  code: string;
  name: string;
  taxId: string | null;
  active: boolean;
  createdAt: string;
}

export function ClientsPage() {
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<ClientRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const crud = useCrud<ClientRow, ClientInput>({
    basePath: '/admin/clients',
    queryKey: ['clients'],
    listParams: { active: 'all', q: search || undefined },
  });

  const columns: Column<ClientRow>[] = [
    { key: 'code', header: 'Code', className: 'font-mono font-medium' },
    { key: 'name', header: 'Name', grow: true },
    {
      key: 'taxId',
      header: 'Tax ID',
      render: (r) => r.taxId ?? <span className="text-brand-gray-mid">—</span>,
    },
    {
      key: 'active',
      header: 'Status',
      render: (r) => (
        <Badge tone={r.active ? 'success' : 'neutral'}>
          {r.active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
  ];

  return (
    <div className="mx-auto max-w-6xl">
      <CrudHeader
        title="Clients"
        description="Billable entities. Soft-delete preserves history."
        searchValue={search}
        onSearchChange={setSearch}
        onCreate={() => setCreating(true)}
        createLabel="New client"
        extraActions={
          <Button variant="secondary" onClick={() => setBulkOpen(true)}>
            Bulk upload
          </Button>
        }
      />

      <DataTable
        columns={columns}
        rows={crud.list.data?.items}
        loading={crud.list.isPending}
        total={crud.list.data?.total}
        rowKey={(r) => r.id}
        actions={(r) => (
          <div className="flex justify-end gap-1">
            <Button variant="ghost" size="sm" onClick={() => setEditing(r)}>
              <Pencil className="h-3.5 w-3.5" aria-hidden />
            </Button>
            {r.active && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (confirm(`Deactivate client "${r.code} — ${r.name}"?`)) {
                    crud.remove.mutate(r.id);
                  }
                }}
              >
                <Power className="h-3.5 w-3.5 text-brand-danger" aria-hidden />
              </Button>
            )}
          </div>
        )}
      />

      <ClientFormModal
        open={creating}
        onClose={() => setCreating(false)}
        onSubmit={async (input) => {
          await crud.create.mutateAsync(input);
          setCreating(false);
        }}
        title="New client"
      />

      <ClientFormModal
        open={!!editing}
        onClose={() => setEditing(null)}
        onSubmit={async (input) => {
          if (!editing) return;
          await crud.update.mutateAsync({ id: editing.id, input });
          setEditing(null);
        }}
        title={`Edit client — ${editing?.code ?? ''}`}
        defaultValues={
          editing
            ? {
                code: editing.code,
                name: editing.name,
                taxId: editing.taxId ?? '',
                active: editing.active,
              }
            : undefined
        }
      />

      <BulkUpload
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        title="Bulk upload clients (CSV)"
        endpoint="/admin/bulk/clients"
        invalidateKey={['clients']}
        sampleHeaders={['code', 'name', 'taxId', 'active']}
        rowSchema={clientInputSchema}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Client form modal
// ---------------------------------------------------------------------------

interface ClientFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: ClientInput) => Promise<void>;
  title: string;
  defaultValues?: Partial<ClientInput>;
}

function ClientFormModal({ open, onClose, onSubmit, title, defaultValues }: ClientFormModalProps) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ClientInput>({
    resolver: zodResolver(clientInputSchema),
    defaultValues: {
      code: '',
      name: '',
      taxId: '',
      active: true,
      ...defaultValues,
    },
  });

  // Reset when defaultValues change (edit different row).
  // useEffect would be cleaner, but we use `key` on the parent to force remount instead.

  const submit = handleSubmit(async (values) => {
    try {
      await onSubmit(values);
      reset();
    } catch (e) {
      // Errors surfaced via the Alert below — parse from thrown
      const msg = (e as Error).message;
      alert(msg);
    }
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button onClick={submit} loading={isSubmitting} type="button">
            Save
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Code" error={errors.code?.message} required hint="A-Z, 0-9, underscore, dash">
          {(p) => <Input {...p} {...register('code')} placeholder="ACME" />}
        </Field>
        <Field label="Name" error={errors.name?.message} required>
          {(p) => <Input {...p} {...register('name')} placeholder="Acme Corporation Ltd." />}
        </Field>
        <Field label="Tax ID" error={errors.taxId?.message} hint="Optional, max 20 chars">
          {(p) => <Input {...p} {...register('taxId')} placeholder="0105561234567" />}
        </Field>
        <Checkbox label="Active" {...register('active')} defaultChecked />
      </form>
    </Modal>
  );
}
